import angular from 'angular';

import r2a from '@web/angularIntegration/r2a';
import ScheduleReact from '@web/shifts/windowed/ScheduleWindowedWithContext';
import LaborInformationBoxReact from '@web/labor/LaborInformationBox/LaborInformationBoxAngular';
import AbsenceSummaryInformationBoxReact from '@web/labor/AbsenceSummaryInformationBox/AbsenceSummaryInformationBoxAngular';
import ShiftsSummaryInformationBoxReact from '@web/labor/ShiftsSummaryInformationBox/ShiftsSummaryInformationBoxAngular';
import InviteEmployeesModal from '@web/users/InviteUsers/InviteEmployeesModalAngular';
import EnableIcalModalReact from '@web/shifts/EnableIcalModal/EnableIcalModalAngular';
import ActivityLogReact from '@web/shifts/ActivityLog/ActivityLogAngular';
import ImportToastShiftsDropdownAngular from '@web/components/ImportToastShiftsDropdown/ImportToastShiftsDropdown';

import InviteEmployeesTrigger from '@angular/shifts/components/inviteEmployeesTriggerComponent';

import { accessLevels } from '../constants';

import indexUrl from './index.html';
import CalendarImport from './models/Calendar';
import ShiftLogsImport from './models/ShiftLogs';
import LeaveImport from './models/Leave';
import AvailabilityImport from './models/Availability';
import SuppressionsImport from './models/Suppressions';
import NotesImport from './models/Notes';
import EventCollectionImport from './models/EventCollection';
import ShiftsEducationImport from './services/ShiftsEducation';
import ShiftHeaderUtilsImport from './services/ShiftHeaderUtils';
import AskSlotsService from './services/AskSlotsService';
import ShiftUtilsImport from './ShiftUtils';
import CalendarNavigationFiltersImport from './services/CalendarNavigationFilters';
import AbsenceUtilsImport from './services/AbsenceUtils';
import EventProcessorServiceImport from './services/EventProcessorService';
import LeaveService from './services/LeaveService';
import TagsServiceImport from './services/TagsService';
import SidebarFiltersService from './services/SidebarFiltersService';
import TimeBlocksService from './services/TimeBlocksService';
import AutomatedBreaksService from './services/AutomatedBreaksService';
import resizableShift from './directives/resizableShift';
import shiftForm from './directives/shiftForm';
import shiftHistory from './directives/shiftHistory';
import torForm from './directives/torForm';
import userAggregatedEvent from './directives/userAggregatedEvent';
import shiftsStickyContainer from './directives/shiftsStickyContainer';
import adjustCalendarHeaderWidth from './directives/adjustCalendarHeaderWidth';
import shiftForEducation from './directives/shiftForEducation';
import shiftsStickyUserColumn from './directives/shiftsStickyUserColumn';
import noShowForm from './components/noShowForm';
import sickCalloutForm from './components/sickCalloutForm';
import addAbsenceEvent from './components/addAbsenceEvent';
import absenceView from './components/absenceView';
import noShowUserRow from './components/noShowUserRow';
import coverShiftOptions from './components/coverShiftOptions';
import calendarNotesComponent from './components/calendarNotesComponent';
import recurrenceOptionsComponent from './components/recurrenceOptionsComponent';
import recurrenceRepeatingDaysComponent from './components/recurrenceRepeatingDaysComponent';
import dayModeEventsRowComponent from './components/dayModeEventsRowComponent';
import subNavigationComponent from './components/subNavigationComponent';
import availabilitiesTabComponent from './components/availabilitiesTabComponent';
import unavailabilityListScreenComponent from './components/unavailabilityListScreen';
import unavailabilityHistoryScreenComponent from './components/unavailabilityHistoryScreen';
import unavailabilityDetailsViewComponent from './components/unavailabilityDetailsView';
import shiftsCheckboxSelectorComponent from './components/shiftsCheckboxSelectorComponent';
import positionsOptionsComponent from './components/filters/positionsOptionsComponent';
import eventTypeOptionsComponent from './components/filters/eventTypeOptionsComponent';
import genericOptionsComponent from './components/filters/genericOptionsComponent';
import optionsFilterComponent from './components/filters/optionsFilterComponent';
import dropdownFilterComponent from './components/filters/dropdownFilterComponent';
import filterHeaderTitleComponent from './components/filters/filterHeaderTitleComponent';
import calendarPrintHeaderComponent from './components/calendarHeader/calendarPrintHeaderComponent';
import printDayHeaderComponent from './components/calendarHeader/printDayHeaderComponent';
import printWeekHeaderComponent from './components/calendarHeader/printWeekHeaderComponent';
import printMonthHeaderComponent from './components/calendarHeader/printMonthHeaderComponent';
import calendarHeaderComponent from './components/calendarHeader/calendarHeaderComponent';
import calendarHeaderDayComponent from './components/calendarHeader/calendarHeaderDayComponent';
import calendarHeaderWeekComponent from './components/calendarHeader/calendarHeaderWeekComponent';
import calendarHeaderMonthComponent from './components/calendarHeader/calendarHeaderMonthComponent';
import calendarRowUserComponent from './components/calendarRowUserComponent';
import shiftBoxIconComponent from './components/shiftBoxIconComponent';
import schedulingConflictsComponent from './components/schedulingConflictsComponent';
import salesEditComponent from './components/salesEditComponent';
import salesSummaryComponent from './components/salesSummaryComponent';
import pendingApprovalSummaryComponent from './components/calendarHeader/pendingApprovalSummaryComponent';
import dayOptionsComponent from './components/dayOptionsComponent';
import shiftsTipsComponent from './components/shiftsTipsComponent';
import applicantRowContentComponent from './components/applicantRowContentComponent';
import leaveTypesSelectionRowComponent from './components/leaveTypesSelectionRowComponent';
import salesImportTableComponent from './components/salesImportTableComponent';
import endTimePickerComponent from './components/endTimePickerComponent';
import leaveTypePickerComponent from './components/leaveTypePickerComponent';
import moreOptionsMenuComponent from './components/moreOptionsMenuComponent';
import calendarStatsComponent from './components/calendarStatsComponent';
import ScheduleTemplatesService from './schedule-templates/ScheduleTemplatesService';
import dynamicLaborSummaryItemComponent from './components/dynamicLaborSummaryItemComponent';
import dynamicLaborValueComponent from './components/dynamicLaborValueComponent';
import publishWarningModalComponent from './components/publishWarningModalComponent';
import publishWarningLaborCostComponent from './components/publishWarningLaborCostComponent';
import shiftsExportOptionsComponent from './components/shiftsExportOptionsComponent';
import shiftTemplatesComponent from './components/shiftTemplatesComponent';
import shiftTemplatePreviewComponent from './components/shiftTemplatePreviewComponent';
import shiftTemplateSelectionComponent from './components/shiftTemplateSelectionComponent';
import shiftTemplateSelectionGroupComponent from './components/shiftTemplateSelectionGroupComponent';
import shiftBoxMiniIconsComponent from './components/shiftBoxMiniIconsComponent';
import taskTemplatesSidebarComponent from './components/taskTemplatesSidebarComponent';
import timeBlocksSidebarComponent from './components/timeBlocksSidebarComponent';
import viewTaskTemplateSidebarComponent from './components/modals/viewTaskTemplateSidebarComponent';
import eventTinyComponent from './components/eventTinyComponent';
import shiftSlotsComponent from './components/shiftSlotsComponent';
import eventTinyLineWithClockComponent from './components/eventTinyLineWithClockComponent';
import fieldsVisibilityButtonsComponent from './components/fieldsVisibilityButtonsComponent';
import UserSearchBarComponent from './shift-swap/UserSearchBarComponent';
import SearchBarComponent from './shift-swap/SearchBarComponent';
import SelectableUserRowComponent from './shift-swap/SelectableUserRowComponent';
import SelectableUsersGroupComponent from './shift-swap/SelectableUsersGroupComponent';
import SelectableShiftRowComponent from './shift-swap/SelectableShiftRowComponent';
import SelectableShiftsGroupComponent from './shift-swap/SelectableShiftsGroupComponent';
import SelectableRowContentComponent from './shift-swap/SelectableRowContentComponent';
import PickUserStepComponent from './shift-swap/PickUserStepComponent';
import PickShiftStepComponent from './shift-swap/PickShiftStepComponent';
import ShiftSwapDetailsComponent from './shift-swap/ShiftSwapDetailsComponent';
import ShiftSwapHourDiffIndicatorComponent from './shift-swap/ShiftSwapHourDiffIndicatorComponent';
import ShiftAcceptanceUtilsImport from './shift-acceptance/ShiftAcceptanceUtils';
import ShiftAcceptanceDetailsComponent from './shift-acceptance/ShiftAcceptanceDetailsComponent';
import ShiftAcceptanceIndicatorComponent from './shift-acceptance/ShiftAcceptanceIndicatorComponent';
import ShiftAcceptanceBulkButtonComponent from './shift-acceptance/ShiftAcceptanceBulkButtonComponent';
import ShiftAcceptanceInfoIconComponent from './shift-acceptance/ShiftAcceptanceInfoIconComponent';
import PossibleAssigneesFactory from './PossibleAssignees';
import modalShiftViewUrl from './modal-shift-view.html';
import ToastShiftsService from './services/ToastShiftsService';

const shiftsModule = angular.module('sling.shifts', [
  'ui.router',
  'components.security',
  'components.services',
  'components.filters',
  'sling.dashboard',
  'restangular',
  'cfp.loadingBar',
]);

shiftsModule.config(($stateProvider) => {
  $stateProvider.state('shifts', {
    url: '/shifts?date?mode?tab?notificationId',
    reloadOnSearch: false,
    controllerAs: 'ctrl',
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "shifts" */ './ShiftsCtrl')).default,
    templateUrl: indexUrl,
    params: {
      openIcalTooltip: null,
    },
    data: {
      title: 'Shifts',
      access: accessLevels.user,
    },
    resolve: {
      shiftUtils(ShiftUtils) {
        return ShiftUtils.loadAsync();
      },
      serializedState: () => {},
      personaCollection(PersonaCollection) {
        return PersonaCollection.loadData();
      },
      tags(TagsService) {
        return TagsService.loadData();
      },
      dayParts(DayPartsService) {
        return DayPartsService.loadData();
      },
      laborCalendarUISchema(LaborUISchema) {
        return LaborUISchema.getSchema();
      },
      taskTemplates(Authentication, ShiftTasksService, TaskTemplatesService) {
        if (Authentication.user.isManager && ShiftTasksService.isFeatureEnabled) {
          return TaskTemplatesService.loadTemplates();
        }
      },
    },
    onEnter(
      AnnouncementService,
      Authentication,
      ShiftsEducation,
      NotificationCollection,
      NotificationModal,
      RateService,
      $location,
      $timeout
    ) {
      ShiftsEducation.init();
      RateService.triggerRateModal();

      /**
       * the async IIFE trick is used to not stall the loading of the state
       * otherwise if onEnter was async ui-router would wait until it resolves
       */

      if (
        !Authentication.user.settings.adminOnboardingCompleted &&
        Authentication.user.isManager &&
        ShiftsEducation.shouldShowOnboarding()
      ) {
        (async () => {
          await AnnouncementService.allShown();
          await $timeout(1000);

          ShiftsEducation.showOnboarding();
        })();
        return;
      }

      (async () => {
        if (
          !Authentication.user.settings.clientFlags.shiftsIntroModalShown &&
          !Authentication.user.isManager
        ) {
          await ShiftsEducation.showShiftsIntro({ isManager: false });
        }

        await $timeout();
        const { notificationId } = $location.search();
        if (notificationId) {
          const notification = await NotificationCollection.retrieveNotification(notificationId, {
            ignoreGlobalErrorHandler: true,
          });
          if (notification) {
            NotificationModal.openNotificationModal(notification);
          }
        }
      })();
    },
    onExit(ShiftsEducation, ShiftUtils, $rootScope, $document) {
      ShiftUtils.viewDatesParam = null;
      if ($rootScope.isFullscreen) {
        $document[0].exitFullscreen();
      }
    },
  });

  $stateProvider.state('shifts.details', {
    url: '/details/:id',
    params: {
      shift: null,
      fromExchange: false,
      activityLogItem: null,
    },
    async onEnter(EventCollection, extendShiftModel, $uibModal, $state, $stateParams) {
      const { shift, id, fromExchange, activityLogItem } = $stateParams;

      const extendedShift = shift || (await getShift(EventCollection, extendShiftModel, id));
      if (!extendedShift) {
        $state.go('shifts', {}, { reload: false });
      }

      $uibModal
        .open({
          name: 'ViewShiftCtrl',
          templateUrl: __TEST__ ? 'TEMPLATE' : modalShiftViewUrl,
          controllerProvider: async () =>
            (await import(/* webpackChunkName: "shifts" */ './ViewShiftCtrl')).default,
          controllerAs: 'ctrl',
          size: '620',
          resolve: {
            shift() {
              return extendedShift;
            },
            notification() {
              return false;
            },
            activityLogItem() {
              return activityLogItem;
            },
            fromExchange() {
              return fromExchange;
            },
          },
        })
        .result.catch(() => $state.go('shifts', {}, { reload: false }));
    },
  });

  $stateProvider.state('shifts.unavailability', {
    url: '/unavailability/:id',
    params: {
      userId: null,
      day: null,
    },
    views: {
      'unavailability@shifts': {
        template: '<unavailability-details-view></unavailability-details-view>',
      },
    },
  });
});

async function getShift(EventCollection, extendShiftModel, shiftId) {
  if (!shiftId) {
    return null;
  }

  try {
    const extended = extendShiftModel(await EventCollection.getEvent(shiftId));
    return extended;
  } catch (e) {
    return null;
  }
}

shiftsModule.factory('Calendar', CalendarImport);
shiftsModule.factory('ShiftLogs', ShiftLogsImport);
shiftsModule.factory('Leave', LeaveImport);
shiftsModule.factory('Availability', AvailabilityImport);
shiftsModule.factory('Suppressions', SuppressionsImport);
shiftsModule.factory('Notes', NotesImport);
shiftsModule.factory('extendCalendarModel', (sdk) => sdk('model-extensions/extendCalendarModel'));
shiftsModule.factory('extendShiftModel', (sdk) => sdk('model-extensions/extendShiftModel'));
shiftsModule.factory('extendLeaveModel', (sdk) => sdk('model-extensions/extendLeaveModel'));
shiftsModule.factory('extendAvailabilityModel', (sdk) =>
  sdk('model-extensions/extendAvailabilityModel')
);
shiftsModule.factory('extendDayMarkModel', (sdk) => sdk('model-extensions/extendDayMarkModel'));

shiftsModule.service('EventCollection', EventCollectionImport);
shiftsModule.service('ShiftsEducation', ShiftsEducationImport);
shiftsModule.service('ShiftHeaderUtils', ShiftHeaderUtilsImport);
shiftsModule.service('AskSlotsService', AskSlotsService);
shiftsModule.service('ShiftUtils', ShiftUtilsImport);
shiftsModule.service('ShiftAcceptanceUtils', ShiftAcceptanceUtilsImport);
shiftsModule.service('CalendarNavigationFilters', CalendarNavigationFiltersImport);
shiftsModule.service('AbsenceUtils', AbsenceUtilsImport);
shiftsModule.service('EventProcessorService', EventProcessorServiceImport);
shiftsModule.service('LeaveService', LeaveService);
shiftsModule.service('AutomatedBreaksService', AutomatedBreaksService);
shiftsModule.service('TagsService', TagsServiceImport);
shiftsModule.service('SidebarFiltersService', SidebarFiltersService);
shiftsModule.service('ScheduleTemplatesService', ScheduleTemplatesService);
shiftsModule.service('TimeBlocksService', TimeBlocksService);
shiftsModule.service('ToastShiftsService', ToastShiftsService);

shiftsModule.directive('resizableShift', resizableShift);
shiftsModule.directive('shiftForm', shiftForm);
shiftsModule.directive('shiftHistory', shiftHistory);
shiftsModule.directive('torForm', torForm);
shiftsModule.directive('userAggregatedEvent', userAggregatedEvent);
shiftsModule.directive('shiftsStickyContainer', shiftsStickyContainer);
shiftsModule.directive('adjustCalendarHeaderWidth', adjustCalendarHeaderWidth);
shiftsModule.directive('shift', shiftForEducation);
shiftsModule.directive('shiftsStickyUserColumn', shiftsStickyUserColumn);

shiftsModule.component('calendarNotes', calendarNotesComponent);
shiftsModule.component('recurrenceOptions', recurrenceOptionsComponent);
shiftsModule.component('recurrenceRepeatingDays', recurrenceRepeatingDaysComponent);
shiftsModule.component('dayModeEventsRow', dayModeEventsRowComponent);
shiftsModule.component('subNavigation', subNavigationComponent);
shiftsModule.component('availabilitiesTab', availabilitiesTabComponent);
shiftsModule.component('shiftsCheckboxSelector', shiftsCheckboxSelectorComponent);
shiftsModule.component('positionsOptions', positionsOptionsComponent);
shiftsModule.component('eventTypeOptions', eventTypeOptionsComponent);
shiftsModule.component('genericOptions', genericOptionsComponent);
shiftsModule.component('optionsFilter', optionsFilterComponent);
shiftsModule.component('dropdownFilter', dropdownFilterComponent);
shiftsModule.component('filterHeaderTitle', filterHeaderTitleComponent);
shiftsModule.component('calendarPrintHeader', calendarPrintHeaderComponent);
shiftsModule.component('printDayHeader', printDayHeaderComponent);
shiftsModule.component('printWeekHeader', printWeekHeaderComponent);
shiftsModule.component('printMonthHeader', printMonthHeaderComponent);
shiftsModule.component('calendarHeader', calendarHeaderComponent);
shiftsModule.component('calendarHeaderDay', calendarHeaderDayComponent);
shiftsModule.component('calendarHeaderWeek', calendarHeaderWeekComponent);
shiftsModule.component('calendarHeaderMonth', calendarHeaderMonthComponent);
shiftsModule.component('calendarRowUser', calendarRowUserComponent);
shiftsModule.component('shiftBoxIcon', shiftBoxIconComponent);
shiftsModule.component('schedulingConflicts', schedulingConflictsComponent);
shiftsModule.component('salesEdit', salesEditComponent);
shiftsModule.component('salesSummary', salesSummaryComponent);
shiftsModule.component('pendingApprovalSummary', pendingApprovalSummaryComponent);
shiftsModule.component('dayOptions', dayOptionsComponent);
shiftsModule.component('shiftsTips', shiftsTipsComponent);
shiftsModule.component('leaveTypesSelectionRow', leaveTypesSelectionRowComponent);
shiftsModule.component('salesImportTable', salesImportTableComponent);
shiftsModule.component('addAbsenceEvent', addAbsenceEvent);
shiftsModule.component('noShowForm', noShowForm);
shiftsModule.component('sickCalloutForm', sickCalloutForm);
shiftsModule.component('absenceView', absenceView);
shiftsModule.component('noShowUserRow', noShowUserRow);
shiftsModule.component('coverShiftOptions', coverShiftOptions);
shiftsModule.component('endTimePicker', endTimePickerComponent);
shiftsModule.component('leaveTypePicker', leaveTypePickerComponent);
shiftsModule.component('applicantRowContent', applicantRowContentComponent);
shiftsModule.component('moreOptionsMenu', moreOptionsMenuComponent);
shiftsModule.component('calendarStats', calendarStatsComponent);
shiftsModule.component('dynamicLaborSummaryItem', dynamicLaborSummaryItemComponent);
shiftsModule.component('dynamicLaborValue', dynamicLaborValueComponent);
shiftsModule.component('publishWarningModal', publishWarningModalComponent);
shiftsModule.component('publishWarningLaborCost', publishWarningLaborCostComponent);
shiftsModule.component('shiftsExportOptions', shiftsExportOptionsComponent);
shiftsModule.component('shiftTemplates', shiftTemplatesComponent);
shiftsModule.component('shiftTemplatePreview', shiftTemplatePreviewComponent);
shiftsModule.component('taskTemplatesSidebar', taskTemplatesSidebarComponent);
shiftsModule.component('timeBlocksSidebar', timeBlocksSidebarComponent);
shiftsModule.component('viewTaskTemplateSidebar', viewTaskTemplateSidebarComponent);
shiftsModule.component('userSearchBar', UserSearchBarComponent);
shiftsModule.component('searchBar', SearchBarComponent);
shiftsModule.component('selectableUserRow', SelectableUserRowComponent);
shiftsModule.component('selectableUsersGroup', SelectableUsersGroupComponent);
shiftsModule.component('selectableShiftRow', SelectableShiftRowComponent);
shiftsModule.component('selectableShiftsGroup', SelectableShiftsGroupComponent);
shiftsModule.component('selectableRowContent', SelectableRowContentComponent);
shiftsModule.component('pickUserStep', PickUserStepComponent);
shiftsModule.component('pickShiftStep', PickShiftStepComponent);
shiftsModule.component('shiftSwapDetails', ShiftSwapDetailsComponent);
shiftsModule.component('shiftSwapHourDiffIndicator', ShiftSwapHourDiffIndicatorComponent);
shiftsModule.component('shiftAcceptanceDetails', ShiftAcceptanceDetailsComponent);
shiftsModule.component('shiftAcceptanceIndicator', ShiftAcceptanceIndicatorComponent);
shiftsModule.component('shiftAcceptanceBulkButton', ShiftAcceptanceBulkButtonComponent);
shiftsModule.component('shiftAcceptanceInfoIcon', ShiftAcceptanceInfoIconComponent);
shiftsModule.component('shiftTemplateSelection', shiftTemplateSelectionComponent);
shiftsModule.component('shiftTemplateSelectionGroup', shiftTemplateSelectionGroupComponent);
shiftsModule.component('shiftBoxMiniIcons', shiftBoxMiniIconsComponent);
shiftsModule.component('rSchedule', r2a(ScheduleReact));
shiftsModule.component('eventTiny', eventTinyComponent);
shiftsModule.component('shiftSlots', shiftSlotsComponent);
shiftsModule.component('rInviteEmployeesModal', r2a(InviteEmployeesModal));
shiftsModule.component('eventTinyLineWithClock', eventTinyLineWithClockComponent);
shiftsModule.component('fieldsVisibilityButtons', fieldsVisibilityButtonsComponent);
shiftsModule.component('unavailabilityListScreen', unavailabilityListScreenComponent);
shiftsModule.component('unavailabilityHistoryScreen', unavailabilityHistoryScreenComponent);
shiftsModule.component('unavailabilityDetailsView', unavailabilityDetailsViewComponent);
shiftsModule.component('inviteEmployeesTrigger', InviteEmployeesTrigger);
shiftsModule.component('rLaborInformationBox', r2a(LaborInformationBoxReact));
shiftsModule.component('rAbsenceSummaryInformationBox', r2a(AbsenceSummaryInformationBoxReact));
shiftsModule.component('rShiftsSummaryInformationBox', r2a(ShiftsSummaryInformationBoxReact));
shiftsModule.component('rEnableIcalModal', r2a(EnableIcalModalReact));
shiftsModule.component('rActivityLog', r2a(ActivityLogReact));
shiftsModule.component('rImportToastShiftsDropdownAngular', r2a(ImportToastShiftsDropdownAngular));
shiftsModule.factory('PossibleAssignees', PossibleAssigneesFactory);

export default shiftsModule.name;
